import React from "react";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import * as Router from "react-router-dom";
import * as Icons from "react-feather";
import { CopyToClipboard } from "react-copy-to-clipboard";
import queryString from "query-string";
import * as yup from "yup";
import { DashboardLayout } from "component/layout/dashboard";
import { SettingsSection } from "component/shared/settings-page";
import { FormField, FormSelectField } from "component/shared/form-fields";
import { FeatherIcon } from "component/shared/feather-icon";
import { SettingsStickyFooter } from "component/shared/settings-sticky-footer";
import { UnsavedChangesPrompt } from "component/shared/unsaved-changes-prompt";
import { BrandCheckbox } from "component/shared/brand-checkbox";
import { SettingsDialog } from "component/shared/settings-dialog";
import { PageProps } from "shared/page-props";
import * as env from "shared/env";
import * as paths from "routes/paths";
import { User } from "model/user";
import { validate } from "shared/yup";
import { formatPhoneNumber, normalizePhoneNumberInput } from "shared/phone-number-formatter";
import { stateOptionsFor } from "shared/us-states";
import { saveUser } from "redux/slice/authentication";
import { fetchBilling } from "redux/slice/billing";
import { RootState } from "redux/store";
import { BillingTab } from "../billing";
import { SUPPORT_EMAIL, SUPPORT_PHONE } from "../support-contact";
import { styles } from "./refreshed-style";

type AccountTab = "general" | "billing";

const NOTIFICATION_EMAIL_TOOLTIP =
	"Includes lead activity notifications and lead activity digests (daily, weekly, and monthly " +
	"summaries). Paperwork and contracts aren't affected — those still go to your account email.";

const CONTACT_PERSON_TOOLTIP =
	"Use this if someone other than the account owner — like an office manager or assistant — " +
	"should be the point of contact for this account.";

const mapStateToProps = (state: RootState) => ({
	authLoading: state.authentication.loading,
	authError: state.authentication.error,
	billingLoaded: !!state.billing.summary,
	billingError: !!state.billing.error,
	billingEligible: !!state.billing.summary?.eligible,
});

const mapDispatchToProps = (dispatch: Dispatch) => bindActionCreators({ saveUser, fetchBilling }, dispatch);

interface Props
	extends PageProps,
		Router.RouteComponentProps,
		WithStyles<typeof styles>,
		ReturnType<typeof mapStateToProps>,
		ReturnType<typeof mapDispatchToProps> {
	user: User;
}

interface EditableFields {
	companyEmail: string;
	notificationEmail: string;
	companyPhone: string;
	clientCellPhoneNumber: string;
	companyFax: string;
	companyAddress: string;
	companyCity: string;
	companyState: string;
	companyZip: string;
	baseUrl: string;
	contactName: string;
	contactEmail: string;
	contactPhone: string;
}

interface State extends EditableFields {
	notificationEmailEnabled: boolean;
	contactPersonEnabled: boolean;
	showValidation: boolean;
	saving: boolean;
	snackbarOpen: boolean;
	cancelModalOpen: boolean;
	copied: boolean;
	tab: AccountTab;
}

const isPhoneNumber = (value: string) => {
	const digits = value.replace(/\D/g, "");
	return digits.length === 10 || (digits.length === 11 && digits.startsWith("1"));
};

// Numbers saved before the form formatted as you type show in the same (555) 555-5555 shape (MAX-4510).
// Anything that isn't a full number is left as stored, since the formatter mangles partial ones.
const phone = (value?: string | null) => (value && isPhoneNumber(value) ? formatPhoneNumber(value) : value ?? "");

function fieldsFromUser(user: User): EditableFields {
	return {
		companyEmail: user.companyEmail ?? "",
		notificationEmail: user.notificationEmail ?? "",
		companyPhone: phone(user.companyPhone),
		clientCellPhoneNumber: phone(user.clientCellPhoneNumber),
		companyFax: phone(user.companyFax),
		companyAddress: user.companyAddress ?? "",
		companyCity: user.companyCity ?? "",
		companyState: user.companyState ?? "",
		companyZip: user.companyZip ?? "",
		baseUrl: user.baseUrl ?? "",
		contactName: user.contactName ?? "",
		contactEmail: user.contactEmail ?? "",
		contactPhone: phone(user.contactPhone),
	};
}

/**
 * The Contact Person section is revealed when it holds something genuinely different from the
 * Account Owner, rather than merely being populated — most accounts carry a contact email that
 * just repeats the account email, and showing that back is noise.
 *
 * The comparison runs on every load instead of being stored, because either side can change
 * independently through admin tools or the SOAP API.
 */
function contactPersonDiffersFromOwner(user: User): boolean {
	const normalize = (value?: string) => (value ?? "").trim().replace(/\s+/g, " ").toLowerCase();
	const digits = (value?: string) => (value ?? "").replace(/\D/g, "");
	const ownerNames = [
		normalize([user.clientFirstName, user.clientLastName].filter(Boolean).join(" ")),
		normalize(user.clientName),
	].filter(Boolean);
	const ownerPhones = [digits(user.companyPhone), digits(user.clientCellPhoneNumber)].filter(Boolean);
	const contactName = normalize(user.contactName);
	const contactEmail = normalize(user.contactEmail);
	const contactPhone = digits(user.contactPhone);
	const nameDiffers = !!contactName && !ownerNames.includes(contactName);
	const emailDiffers = !!contactEmail && contactEmail !== normalize(user.companyEmail);
	const phoneDiffers = !!contactPhone && !ownerPhones.includes(contactPhone);
	return nameDiffers || emailDiffers || phoneDiffers;
}

class Component extends React.Component<Props, State> {

	public constructor(props: Props) {
		super(props);
		this.state = {
			...fieldsFromUser(props.user),
			notificationEmailEnabled: !!props.user.notificationEmail,
			contactPersonEnabled: contactPersonDiffersFromOwner(props.user),
			showValidation: false,
			saving: false,
			snackbarOpen: false,
			cancelModalOpen: false,
			copied: false,
			tab: this.tabFromSearch(props.location.search),
		};
	}

	public componentDidMount() {
		if (env.billingRefresh && (this.props.user.admin || this.props.user.isAccountOwner)) {
			this.props.fetchBilling();
		}
	}

	public componentDidUpdate(prevProps: Props) {
		const { authLoading, authError, location } = this.props;
		if (prevProps.authLoading && !authLoading && !authError && this.state.saving) {
			this.setState({ saving: false });
		}
		if (prevProps.location.search !== location.search) {
			this.setState({ tab: this.tabFromSearch(location.search) });
		}
	}

	// Read from ?tab=billing (the redirect from /settings/billing) but never written back, so
	// switching tabs doesn't trip UnsavedChangesPrompt, which drops the query string.
	private tabFromSearch(search: string): AccountTab {
		return env.billingRefresh && queryString.parse(search).tab === "billing" && (this.props.user.admin || this.props.user.isAccountOwner) ? "billing" : "general";
	}

	private validationSchema = {
		companyEmail: yup.string().email("Invalid Email").required("This field is required"),
		notificationEmail: yup.string().email("Invalid Email"),
		contactEmail: yup.string().email("Invalid Email"),
		clientCellPhoneNumber: yup
			.string()
			// Count digits, not characters: a stored 555-555-5555 is as valid as (555) 555-5555.
			.test("phone", "Invalid phone number", (value) => !value || isPhoneNumber(value)),
	};

	private isDirty(): boolean {
		const original = fieldsFromUser(this.props.user);
		return (Object.keys(original) as Array<keyof EditableFields>)
			.some((key) => original[key] !== this.state[key]);
	}

	private handleFieldChange = (field: keyof EditableFields) =>
		(event: React.ChangeEvent<HTMLInputElement>) => {
			this.setState({ [field]: event.target.value } as unknown as Pick<State, keyof EditableFields>);
		};

	private handlePhoneChange = (field: keyof EditableFields) =>
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const formatted = normalizePhoneNumberInput(event.target.value, this.state[field]);
			this.setState({ [field]: formatted ?? event.target.value } as unknown as Pick<State, keyof EditableFields>);
		};

	private handleNotificationEmailToggle = (checked: boolean) => {
		// Clearing sends "" rather than undefined: the API applies fields only when non-null,
		// so undefined would leave the stored value in place.
		this.setState({
			notificationEmailEnabled: checked,
			notificationEmail: checked ? this.state.notificationEmail : "",
		});
	};

	private handleContactPersonToggle = (checked: boolean) => {
		// Unchecking hides the section but must not erase stored values — only an explicit edit
		// to the fields themselves should do that.
		this.setState({ contactPersonEnabled: checked });
	};

	private handleSave = () => {
		const { saveUser, user } = this.props;
		const errors = validate(this.validationSchema, this.state);
		if (errors) {
			this.setState({ showValidation: true });
			return;
		}
		const fields = fieldsFromUser(this.props.user);
		const edited = (Object.keys(fields) as Array<keyof EditableFields>)
			.reduce((result, key) => ({ ...result, [key]: this.state[key] }), {});
		saveUser({ user: { ...user, ...edited } });
		this.setState({ saving: true, snackbarOpen: true, showValidation: false });
	};

	private handleCancel = () => {
		const { user } = this.props;
		this.setState({
			...fieldsFromUser(user),
			notificationEmailEnabled: !!user.notificationEmail,
			contactPersonEnabled: contactPersonDiffersFromOwner(user),
			showValidation: false,
		});
	};

	private cancellationMessage(): string {
		const { user } = this.props;
		return [
			"Hello,",
			"I would like to cancel my iHomefinder Account",
			`CID: ${user.clientId}`,
			`Email Address: ${user.companyEmail ?? ""}`,
			`Account Name: ${user.name ?? ""}`,
		].join("\n");
	}

	private renderReadOnly(label: string, value?: string) {
		const { classes = {} } = this.props;
		return (
			<Mui.Grid item xs={12}>
				<Mui.Typography className={classes.readOnlyLabel}>{label}</Mui.Typography>
				<Mui.Typography className={classes.readOnlyValue}>{value || "—"}</Mui.Typography>
			</Mui.Grid>
		);
	}

	private renderBillingBanner() {
		const { user, classes = {} } = this.props;
		return (
			<Mui.Box mb={2}>
				<Mui.Alert severity="info" icon={<Icons.Info size={20} />}>
					<Mui.Typography className={classes.bannerBody}>
						If you need to update your payment method, plan, or account ownership, contact our
						support team. Note that some changes may require additional MLS paperwork.
					</Mui.Typography>
					<Mui.Box className={classes.bannerContactRow}>
						<Icons.Mail size={16} />
						<span>Email: {SUPPORT_EMAIL}</span>
					</Mui.Box>
					<Mui.Box className={classes.bannerContactRow}>
						<Icons.Phone size={16} />
						<span>Phone: {SUPPORT_PHONE}</span>
					</Mui.Box>
					<Mui.Typography className={classes.bannerClientId}>
						For faster assistance, please provide your client ID: {user.clientId}
					</Mui.Typography>
				</Mui.Alert>
			</Mui.Box>
		);
	}

	private renderCurrentPlan() {
		const { user, classes = {} } = this.props;
		const userPackage = user.userPackage;
		if (!userPackage || !userPackage.showPackageName) {
			return null;
		}
		const price = userPackage.packagePrice;
		const term = user.annual ? "per year" : "per month";
		const label = price
			? `${userPackage.packageName} - $${price} ${term}`
			: userPackage.packageName;
		return (
			<SettingsSection title="Current Plan">
				<Mui.Typography className={classes.planValue}>{label}</Mui.Typography>
			</SettingsSection>
		);
	}

	private renderAccountOwner() {
		const { user, classes = {} } = this.props;
		return (
			<SettingsSection
				title="Account Owner"
				description="The person or company this account is registered to. Used for login, billing, and MLS compliance."
				fieldColumn
			>
				<Mui.Grid container spacing={2}>
						{this.renderReadOnly("First Name", user.clientFirstName)}
						{this.renderReadOnly("Last Name", user.clientLastName)}
						{this.renderReadOnly("Display Name", user.clientName)}
						{this.renderReadOnly("Company Name", user.company)}
						<Mui.Grid item xs={12}>
							<FormField
								label="Website URL"
								placeholder="www.website.com"
								value={this.state.baseUrl}
								onChange={this.handleFieldChange("baseUrl")}
							/>
					</Mui.Grid>
				</Mui.Grid>
			</SettingsSection>
		);
	}

	private renderContactInformation() {
		const { classes = {} } = this.props;
		const { showValidation, notificationEmailEnabled, contactPersonEnabled } = this.state;
		const errors = validate(this.validationSchema, this.state);
		const errorFor = (field: keyof typeof this.validationSchema) =>
			showValidation && errors ? (errors as any)[field] : undefined;
		return (
			<SettingsSection
				title="Contact Information"
				fieldColumn
				description={
					<>
						Used for MLS paperwork, contracts, and lead notifications. Configure lead activity
						notifications under{" "}
						<Router.Link to={paths.settingsEmailRouting}>Notifications &gt; Lead Activity</Router.Link>
						. A different contact person or lead notification email can be set below.
					</>
				}
			>
				<Mui.Grid container spacing={2}>
						<Mui.Grid item xs={12}>
							<FormField
								label="Email"
								placeholder="you@example.com"
								required
								value={this.state.companyEmail}
								onChange={this.handleFieldChange("companyEmail")}
								error={!!errorFor("companyEmail")}
								helperText={errorFor("companyEmail")}
							/>
						</Mui.Grid>
						<Mui.Grid item xs={12}>
							<Mui.FormControlLabel
								className={classes.checkboxRow}
								control={
									<BrandCheckbox
										checked={notificationEmailEnabled}
										onChange={(event) => this.handleNotificationEmailToggle(event.target.checked)}
									/>
								}
								label={
									<Mui.Box display="flex" alignItems="center" gap={1}>
										<Mui.Typography className={classes.checkboxLabel}>
											Send lead notifications to a different email
										</Mui.Typography>
										<Mui.Tooltip title={NOTIFICATION_EMAIL_TOOLTIP} placement="right" arrow>
											<Mui.IconButton size="small" color="inherit" style={{ padding: 0 }}>
												<Icons.Info size={16} />
											</Mui.IconButton>
										</Mui.Tooltip>
									</Mui.Box>
								}
							/>
							{notificationEmailEnabled && (
								<Mui.Box className={classes.disclosedFields} mt={2}>
									<FormField
										label="Lead Notification Email"
										placeholder="leads@example.com"
										value={this.state.notificationEmail}
										onChange={this.handleFieldChange("notificationEmail")}
										error={!!errorFor("notificationEmail")}
										helperText={errorFor("notificationEmail")}
									/>
								</Mui.Box>
							)}
						</Mui.Grid>
						<Mui.Grid item xs={12}>
							<FormField
								label="Office Phone"
								placeholder="(000) 000-0000"
								value={this.state.companyPhone}
								onChange={this.handlePhoneChange("companyPhone")}
							/>
						</Mui.Grid>
						<Mui.Grid item xs={12}>
							<FormField
								label="Mobile Number"
								placeholder="(000) 000-0000"
								value={this.state.clientCellPhoneNumber}
								onChange={this.handlePhoneChange("clientCellPhoneNumber")}
								error={!!errorFor("clientCellPhoneNumber")}
								helperText={errorFor("clientCellPhoneNumber")}
							/>
						</Mui.Grid>
						<Mui.Grid item xs={12}>
							<FormField
								label="Fax"
								placeholder="(000) 000-0000"
								value={this.state.companyFax}
								onChange={this.handlePhoneChange("companyFax")}
							/>
						</Mui.Grid>
						<Mui.Grid item xs={12}>
							<FormField
								label="Address"
								placeholder="123 Main St."
								value={this.state.companyAddress}
								onChange={this.handleFieldChange("companyAddress")}
							/>
						</Mui.Grid>
						<Mui.Grid item xs={6}>
							<FormField
								label="City"
								placeholder="Eugene"
								value={this.state.companyCity}
								onChange={this.handleFieldChange("companyCity")}
							/>
						</Mui.Grid>
						<Mui.Grid item xs={6}>
							<FormSelectField
								label="State"
								value={this.state.companyState}
								onChange={(value) => this.setState({ companyState: value })}
								options={stateOptionsFor(this.state.companyState)}
								valueExtractor={(option) => option.code}
								labelExtractor={(option) => option.code}
							/>
						</Mui.Grid>
						<Mui.Grid item xs={12}>
							<FormField
								label="ZIP Code"
								placeholder="97401"
								value={this.state.companyZip}
								onChange={this.handleFieldChange("companyZip")}
							/>
						</Mui.Grid>
						<Mui.Grid item xs={12}>
							<Mui.FormControlLabel
								className={classes.checkboxRow}
								control={
									<BrandCheckbox
										checked={contactPersonEnabled}
										onChange={(event) => this.handleContactPersonToggle(event.target.checked)}
									/>
								}
								label={
									<Mui.Box display="flex" alignItems="center" gap={1}>
										<Mui.Typography className={classes.checkboxLabel}>
											This account has a different contact person
										</Mui.Typography>
										<Mui.Tooltip title={CONTACT_PERSON_TOOLTIP} placement="right" arrow>
											<Mui.IconButton size="small" color="inherit" style={{ padding: 0 }}>
												<Icons.Info size={16} />
											</Mui.IconButton>
										</Mui.Tooltip>
									</Mui.Box>
								}
							/>
							{contactPersonEnabled && (
								<Mui.Box className={`${classes.disclosedFields} ${classes.disclosedFieldStack}`}>
									<FormField
										label="Name"
										placeholder="John Doe"
										value={this.state.contactName}
										onChange={this.handleFieldChange("contactName")}
									/>
									<FormField
										label="Email"
										placeholder="jdoe@example.com"
										value={this.state.contactEmail}
										onChange={this.handleFieldChange("contactEmail")}
										error={!!errorFor("contactEmail")}
										helperText={errorFor("contactEmail")}
									/>
									<FormField
										label="Phone"
										placeholder="(123) 456-7890"
										value={this.state.contactPhone}
										onChange={this.handlePhoneChange("contactPhone")}
									/>
								</Mui.Box>
							)}
					</Mui.Grid>
				</Mui.Grid>
			</SettingsSection>
		);
	}

	private showBillingBanner(): boolean {
		const { user, billingLoaded, billingError, billingEligible } = this.props;
		if (!user.reseller.direct) {
			return false;
		}
		if (!env.billingRefresh) {
			return true;
		}
		if (!user.admin && !user.isAccountOwner) {
			return true;
		}
		// An eligible admin or owner updates the card on the Billing tab. Hidden until the summary says
		// otherwise, so it doesn't flash in and out for them.
		return billingLoaded ? !billingEligible : billingError;
	}

	private renderCancelAccount() {
		const { classes = {} } = this.props;
		return (
			<SettingsSection
				title="Cancel Account"
				description="To begin the cancellation process, contact our support team or submit a cancellation request below."
			>
				<Mui.Button
					variant="text"
					className={classes.cancelLink}
					onClick={() => this.setState({ cancelModalOpen: true })}
				>
					Send Cancellation Request
				</Mui.Button>
			</SettingsSection>
		);
	}

	private renderCancelModal() {
		const { classes = {} } = this.props;
		const { cancelModalOpen, copied } = this.state;
		const message = this.cancellationMessage();
		return (
			<SettingsDialog
				open={cancelModalOpen}
				title="Send Cancellation Request"
				onClose={() => this.setState({ cancelModalOpen: false, copied: false })}
			>
				<Mui.Typography>
					Email the following information to{" "}
					<Mui.Link href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</Mui.Link>
				</Mui.Typography>
				<Mui.Box className={classes.cancelModalBlock}>
					<CopyToClipboard text={message} onCopy={() => this.setState({ copied: true })}>
						<Mui.Button
							variant="text"
							color="primary"
							className={classes.cancelModalCopy}
							startIcon={<Icons.Copy size={14} />}
						>
							Copy
						</Mui.Button>
					</CopyToClipboard>
					<Mui.Typography className={classes.cancelModalText}>{message}</Mui.Typography>
				</Mui.Box>
				<Mui.Snackbar
					open={copied}
					autoHideDuration={4000}
					message="Cancellation message copied to clipboard"
					onClose={() => this.setState({ copied: false })}
				/>
			</SettingsDialog>
		);
	}

	private renderTabs() {
		const { classes = {} } = this.props;
		const { tab } = this.state;
		const tabs: { id: AccountTab; label: string }[] = [
			{ id: "general", label: "General" },
			{ id: "billing", label: "Billing" },
		];
		return (
			<div className={classes.tabBar} role="tablist">
				{tabs.map(({ id, label }) => (
					<button
						key={id}
						type="button"
						role="tab"
						aria-selected={id === tab}
						className={`${classes.tab} ${id === tab ? classes.tabActive : ""}`}
						onClick={() => this.setState({ tab: id })}
					>
						<span className={classes.tabLabel}>{label}</span>
						<span className={`${classes.tabUnderline} ${id === tab ? classes.tabUnderlineActive : ""}`} />
					</button>
				))}
			</div>
		);
	}

	private renderGeneral() {
		const { authError, authLoading } = this.props;
		const { saving, snackbarOpen } = this.state;
		return (
			<>
				{this.showBillingBanner() && this.renderBillingBanner()}
				{this.renderCurrentPlan()}
				{this.renderAccountOwner()}
				{this.renderContactInformation()}
				{!env.billingRefresh && this.renderCancelAccount()}
				{!env.billingRefresh && this.renderCancelModal()}

				<SettingsStickyFooter
					dirty={this.isDirty()}
					saving={!!(saving && authLoading)}
					authError={authError ?? null}
					onSave={this.handleSave}
					onCancel={this.handleCancel}
				/>

				<Mui.Snackbar
					open={snackbarOpen && !authLoading && !authError}
					message="Account saved"
					autoHideDuration={6000}
					onClose={() => this.setState({ snackbarOpen: false })}
				/>

				<UnsavedChangesPrompt isDirty={() => this.isDirty()} history={this.props.history} />
			</>
		);
	}

	public render() {
		const { user, classes = {} } = this.props;
		const billingTab = env.billingRefresh && (user.admin || user.isAccountOwner);
		const tab = billingTab ? this.state.tab : "general";
		return (
			<DashboardLayout
				permitted={!!user.admin || (user.type.agent && user.permissions.modifyBio)}
				title="Account"
				header={
					<Mui.Typography variant="h1">
						<FeatherIcon>
							<Icons.Settings />
						</FeatherIcon>
						Account
					</Mui.Typography>
				}
			>
				<Mui.Typography variant="body2" className={classes.pageSubtitle}>
					View your current plan and manage your billing contact information.
				</Mui.Typography>

				{billingTab && this.renderTabs()}
				{tab === "general" ? this.renderGeneral() : <BillingTab user={user} />}
			</DashboardLayout>
		);
	}

}

export const RefreshedAccountInfo = withStyles(
	connect(mapStateToProps, mapDispatchToProps)(Component),
	styles,
);
